import { useTranslation } from 'react-i18next';

import { useDebouncedToast } from 'hooks/useDebouncedToast';
import { AsyncPollerService } from 'services/asyncPoller';
import { usePrefetchSPMRecordsMutation } from 'services/SPM/SPMRecordsPrefetchService';
import type { SPMPrefetchRecordsParams, SPMPrefetchRecordsResponse } from 'types/SPM/spmRecords';

type SharedPrefetchMutationParams = {
  /**
   * Called before starting the async polling operation.
   * Use this to set loading state (e.g., setIsFetchingNewToken(true)).
   */
  onPrefetchStart?: (params: SPMPrefetchRecordsParams) => void;

  /**
   * Called after successfully completing the prefetch and polling.
   * Use this to register the token in your registry.
   *
   * `requestParams` are the params the job was launched with. Compare them against current state
   * before registering — the registry key does not encode the proposed change group, so a token
   * from a superseded change group would otherwise take the slot its successor needs.
   */
  onPrefetchComplete: (token: string, scenarioId: string | undefined, requestParams: SPMPrefetchRecordsParams) => void;

  /**
   * Called when an error occurs during polling.
   * Receives the error and the scenarioId if available.
   */
  onPrefetchError?: (error: unknown, scenarioId: string | undefined) => void;

  /**
   * Called in the finally block, regardless of success or failure.
   * Use this to clear loading state.
   */
  onPrefetchFinally: (scenarioId: string | undefined) => void;

  /**
   * Called when the mutation itself errors (before polling starts).
   */
  onMutationError?: (scenarioId: string | undefined) => void;

  /**
   * Generate the error toast key for deduplication.
   * If not provided, uses a default pattern.
   */
  getErrorToastKey?: (errorMessage: string, scenarioId: string | undefined) => string;
};

/**
 * Shared mutation setup for prefetching SPM records.
 */
export const usePrefetchMutation = (params: SharedPrefetchMutationParams) => {
  const { showDebouncedErrorToast } = useDebouncedToast();
  const { t } = useTranslation();

  const { onPrefetchStart, onPrefetchComplete, onPrefetchError, onPrefetchFinally, onMutationError, getErrorToastKey } =
    params;

  return usePrefetchSPMRecordsMutation({
    onSuccess: async (data: SPMPrefetchRecordsResponse, requestParams: SPMPrefetchRecordsParams) => {
      const scenarioId = requestParams.scenarioId;

      // Notify caller that prefetch has started
      onPrefetchStart?.(requestParams);

      try {
        // Wait for backend job to complete fetching the token
        await AsyncPollerService.waitForJobAggregationsCompletion(
          'prefetch-records',
          data.aggregation_id,
          true,
          undefined,
          undefined,
          new URLSearchParams({ hidden: 'true' }),
        );

        // Register token in the registry once prefetch completes
        if (data.prefetch_token) {
          onPrefetchComplete(data.prefetch_token, scenarioId, requestParams);
        }
      } catch (error) {
        // Handle errors during polling
        const errorMessage =
          error instanceof Error ? error.message : t('SPM.worksheets.errors.prefetch_failed_generic');

        const toastKey = getErrorToastKey
          ? getErrorToastKey(errorMessage, scenarioId)
          : `prefetch-error-${scenarioId || 'unknown'}`;

        showDebouncedErrorToast(errorMessage, undefined, toastKey);

        // Notify caller of the error
        onPrefetchError?.(error, scenarioId);
      } finally {
        // Always notify caller in finally block
        onPrefetchFinally(scenarioId);
      }
    },
    onError: (_, variables: SPMPrefetchRecordsParams) => {
      // Called when the mutation itself fails (before polling)
      onMutationError?.(variables.scenarioId);
    },
  });
};
